import { useCallback, useRef, useState } from "react";
import type { ApiResponse } from "../../http-client/Response";
import type { ComparisonsDict } from "../../i18n/dictionaries/en/comparisons";
import type { ComparisonListItem } from "../../services/Comparisons";
import { MAX_STANDARD_AGREEMENT_BYTES } from "../../services/StandardAgreements";
import type { StandardAgreement } from "../../services/StandardAgreements";

export const AUTO_ROLE = "auto";
export const ROLES = [AUTO_ROLE, "customer", "supplier", "discloser", "recipient", "mutual"];
export const COMPARABLE_FILE = /\.(docx|pdf)$/i;

export const isReady = (s: StandardAgreement) => s.currentVersion?.status === "ready";
export const isPreparing = (s: StandardAgreement) =>
  s.currentVersion?.status === "preparing" || s.currentVersion?.status === "pending";
export const isFailed = (s: StandardAgreement) => s.currentVersion?.status === "failed";

/** "NDA (2).DOCX" -> "nda.docx": what two copies of the same file have in common */
export const normalizeFileName = (name: string): string =>
  name
    .trim()
    .toLowerCase()
    .replace(/(\s*\(\d+\))+(?=\.[^.]*$|$)/, "");

/** The standard whose original file has this name, if any (a standard dropped as the received agreement) */
export const standardWithFileName = (fileName: string, standards: StandardAgreement[]): StandardAgreement | null => {
  const wanted = normalizeFileName(fileName);
  return standards.find((s) => normalizeFileName(s.fileName) === wanted) ?? null;
};

/** A standard's name defaulted from its file name: "Our_NDA v2.docx" -> "Our NDA v2" */
export const nameFromFileName = (fileName: string): string =>
  fileName
    .replace(/\.[^.]+$/, "")
    .replace(/_+/g, " ")
    .replace(/\s+/g, " ")
    .trim() || fileName;

/** Why a file cannot be compared, in the user's language; null when it can */
export const fileProblem = (file: File, c: ComparisonsDict): string | null => {
  if (!COMPARABLE_FILE.test(file.name)) return c.unsupportedFile;
  if (file.size > MAX_STANDARD_AGREEMENT_BYTES) return c.tooLarge;
  return null;
};

/** The message for a failed start: ours, in the app's language, not the server's text */
export const startErrorMessage = (res: ApiResponse<unknown>, c: ComparisonsDict): string => {
  if (res.status === 404) return c.cp.standardGone;
  if (res.status === 413) return c.tooLarge;
  return c.startFailed;
};

const STOP = new Set([
  "agreement", "agreements", "avtal", "draft", "final", "signed", "copy", "version", "the", "and", "our", "standard",
  "standardavtal", "docx", "pdf", "och", "för", "med", "v1", "v2", "v3", "rev", "utkast",
]);

const tokens = (text: string): string[] =>
  text
    .toLowerCase()
    .split(/[^a-z0-9åäöé]+/i)
    .filter((t) => t.length >= 2 && !STOP.has(t) && !/^\d+$/.test(t));

/**
 * A cheap guess at which standard a document should be compared with:
 * 1. the standard whose name or type shares the most words with the file name;
 * 2. the only ready standard;
 * 3. the one used for the person's latest comparison.
 * Returns null when nothing stands out.
 */
export const suggestStandard = (
  fileName: string | null,
  ready: StandardAgreement[],
  recent: ComparisonListItem[],
): string | null => {
  if (!ready.length) return null;
  if (fileName) {
    const wanted = new Set(tokens(fileName.replace(COMPARABLE_FILE, "")));
    let best: { id: string; score: number } | null = null;
    let tie = false;
    for (const s of ready) {
      const have = new Set(tokens(`${s.name} ${s.agreementType ?? ""}`));
      let score = 0;
      for (const token of have) if (wanted.has(token)) score += 1;
      if (score > 0 && (!best || score > best.score)) {
        best = { id: s.id, score };
        tie = false;
      } else if (score > 0 && best && score === best.score) {
        tie = true;
      }
    }
    if (best && !tie) return best.id;
  }
  if (ready.length === 1) return ready[0].id;
  const last = recent.find((r) => ready.some((s) => s.id === r.standardAgreementId));
  return last?.standardAgreementId ?? null;
};

/** The role used in the person's latest comparison against each standard */
export const lastRoles = (items: ComparisonListItem[]): Map<string, string> => {
  const roles = new Map<string, string>();
  // Newest first, so the first one seen per standard is the latest
  for (const item of items) {
    if (item.ourRole && !roles.has(item.standardAgreementId)) roles.set(item.standardAgreementId, item.ourRole);
  }
  return roles;
};

/** Drag-and-drop handlers for one drop target, with a drag-over flag */
export const useFileDrop = (onFile: (file: File) => void, disabled = false) => {
  const [dragging, setDragging] = useState(false);
  // dragenter/dragleave fire for every child element
  const depth = useRef(0);
  const hasFiles = (e: React.DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");

  const handlers = {
    onDragEnter: useCallback(
      (e: React.DragEvent) => {
        if (disabled || !hasFiles(e)) return;
        e.preventDefault();
        depth.current += 1;
        setDragging(true);
      },
      [disabled],
    ),
    onDragOver: useCallback(
      (e: React.DragEvent) => {
        if (disabled || !hasFiles(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      },
      [disabled],
    ),
    onDragLeave: useCallback((e: React.DragEvent) => {
      if (!hasFiles(e)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setDragging(false);
    }, []),
    onDrop: useCallback(
      (e: React.DragEvent) => {
        if (disabled || !hasFiles(e)) return;
        e.preventDefault();
        depth.current = 0;
        setDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) onFile(file);
      },
      [disabled, onFile],
    ),
  };
  return { dragging, handlers };
};
